import { useState, useCallback } from "react";
import { defineMessages } from "react-intl";

import Button from "../Buttons/Button";
import Icon from "../Utils/Icon";
import { useFormatMessage } from "../Intl/Context";

import { signOut } from "../../mutations/SignOutMutation";

// Displayed to an admin who signed in as a user through an impersonation link,
// so they always know they are browsing as someone else. It can be collapsed to
// an icon-only pill to get it out of the way, but never fully dismissed: the
// viewer must always be able to tell they are still impersonating in this
// window.
const ImpersonationIndicator = () => {
  const [collapsed, setCollapsed] = useState(false);

  const t = useFormatMessage();
  const message = t(intlMessages.message);
  const collapseLabel = t(intlMessages.collapse);
  const expandLabel = t(intlMessages.expand);
  const signOutLabel = t(intlMessages.signOut);

  const handleCollapse = useCallback(() => {
    setCollapsed(true);
  }, []);

  const handleExpand = useCallback(() => {
    setCollapsed(false);
  }, []);

  // End the impersonation by signing the impersonated user out, then land on
  // the page the server points to (the anonymous home by default).
  const handleSignOut = useCallback(() => {
    signOut(({ signOut }) => {
      window.location.href = signOut?.redirectUrl || "/";
    });
  }, []);

  if (collapsed) {
    return (
      <Button
        className="impersonation-indicator-pill bg-warning text-dark fixed-bottom"
        onClick={handleExpand}
        iconClassName="fa fa-user-secret"
        screenReaderLabel={expandLabel}
        data-impersonation-indicator-pill
      />
    );
  }

  return (
    <div
      className="impersonation-indicator bg-warning text-dark fixed-bottom d-flex align-items-center padding-sm"
      role="status"
      data-impersonation-indicator
    >
      <Icon className="fa fa-user-secret margin-right-xs" />

      <p className="flex-1 margin-bottom-none">{message}</p>

      <Button
        className="btn btn-sm btn-dark margin-right-xs"
        onClick={handleSignOut}
        label={signOutLabel}
        data-impersonation-indicator-sign-out
      />

      <Button
        className="impersonation-indicator-collapse close-button text-inherit"
        onClick={handleCollapse}
        iconClassName="fa fa-chevron-down"
        screenReaderLabel={collapseLabel}
        data-impersonation-indicator-collapse
      />
    </div>
  );
};

export default ImpersonationIndicator;

const intlMessages = defineMessages({
  message: {
    id: "impersonation.indicator.message",
    defaultMessage: "Vous êtes connecté à la place d'un utilisateur."
  },
  collapse: {
    id: "impersonation.indicator.collapse",
    defaultMessage: "Réduire"
  },
  expand: {
    id: "impersonation.indicator.expand",
    defaultMessage:
      "Vous êtes connecté à la place d'un utilisateur. Afficher les détails."
  },
  signOut: {
    id: "impersonation.indicator.signOut",
    defaultMessage: "Se déconnecter"
  }
});
